import React, { useEffect, useInsertionEffect, useRef, useState } from "react";
import {
  Canvas,
  IText,
  FabricImage,
  loadSVGFromString,
  util,
  Textbox,
  Rect,
  Pattern,
  Circle,
  Ellipse,
  Polygon,
  Polyline,
  Path,
  Point,
} from "fabric";
import "./ImageToolEditor.scss"; // <--- Import SCSS file here
import PreviewCircleIcon from "../icons/PreviewCircleIcon";
import DownloadIcon from "../icons/DownloadIcon";
import PreviewModal from "./PreviewModal";

const FONT_OPTIONS = ["Poppins", "Inter", "Roboto"];

class CommonEnums {
  static alignment = {
    left: "left",
    center: "center",
    right: "right",
  };

  static shapeTypes = {
    rect: "rect",
    circle: "circle",
    ellipse: "ellipse",
    path: "path",
    polygon: "polygon",
    polyline: "polyline",
    group: "group",
    text: "text",
    textbox: "textbox",
    image: "image",
  };
}

let safeTextInputs = {};

/**
 * Agency logo links from cdn
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/agency-logos/raywhite-agency-logo.png
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/agency-logos/harcourts-3-color-logo.png
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/agency-logos/heritage-logo-3-color-logo.png
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/agency-logos/remax-logo.png
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/agency-logos/top-estates-3-color-logo.png
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/agency-logos/greenview-2-color-logo.png
 */

/**
 * Sample agent profile images links from cdn
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/slwilson.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/sarah-wilson-profile-photo.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/harry-potter-profile-image.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/rose-potter-profile-image.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/john-weak-profile-image.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/eric-pillai-profile-image.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/anna-belle-profile-image.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/profile-images/150X200-image.avif
 */

/**
 * Sample property images links from cdn
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/property-images/property-image-1.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/property-images/property-image-2.webp
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/property-images/marvin-meyer-bfOQSDwEFg4-unsplash.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/property-images/dillon-kydd-2keCPb73aQY-unsplash.jpg
 * https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/testing-assets/property-images/1000X750-image.webp
 */

/**
 * Text transformation methods
 */

/**
 * Converts a string to Title Case (First Letter of Every Word Capitalized).
 * @param {string} str - The input string.
 * @returns {string} The title-cased string.
 */
function textToTitleCase(str) {
  if (!str) return "";
  var lowerStr = str.toLowerCase();
  var words = lowerStr.split(" ");
  var result = [];

  for (var i = 0; i < words.length; i++) {
    var word = words[i];
    if (word.length === 0) {
      result.push("");
      continue;
    }
    var firstChar = word.charAt(0).toUpperCase();
    var restOfWord = word.substring(1);
    result.push(firstChar + restOfWord);
  }
  return result.join(" ");
}

/**
 * Converts a string to Sentence Case (First letter capitalized, rest lowercase).
 * @param {string} str - The input string.
 * @returns {string} The sentence-cased string.
 */
function textToSentenceCase(str) {
  if (!str) return "";
  var lowerStr = str.toLowerCase();

  // Capitalize only the very first character
  var firstChar = lowerStr.charAt(0).toUpperCase();
  var restOfString = lowerStr.substring(1);

  return firstChar + restOfString;
}

const formatPhoneNumber = (phoneNumber) => {
  // Check if the input is a valid string. If not, return an empty string.
  if (typeof phoneNumber !== "string") {
    return "";
  }

  // Remove any non-digit characters from the string.
  const cleaned = phoneNumber.replace(/\D/g, "");

  // If the number is 9 digits, add a leading '0'.
  // This handles the format where the '0' prefix is missing.
  let formattedNumber = cleaned;
  if (cleaned.length === 9) {
    formattedNumber = "0" + cleaned;
  }

  // Ensure the number is exactly 10 digits before formatting.
  // This is a basic validation to prevent formatting incorrect lengths.
  if (formattedNumber.length === 10) {
    const part1 = formattedNumber.substring(0, 4);
    const part2 = formattedNumber.substring(4, 7);
    const part3 = formattedNumber.substring(7, 10);

    // Join the parts with spaces to create the desired format.
    return `${part1} ${part2} ${part3}`;
  }

  // Return the original string if it doesn't match the expected lengths.
  return phoneNumber;
};

/**
 * Converts a string to Sentence Case (First letter capitalized, rest lowercase).
 * @param {string} layerName - The text layer name.
 * @param {string} inputText - Text value.
 * @returns {string} Validated & transformed string value.
 */
const validateAndTransformTextCase = (layerName, inputText) => {
  var outputText = inputText;

  if (layerName.indexOf("text-uppercase") > -1) {
    outputText = inputText.toUpperCase();
  }

  if (layerName.indexOf("text-lowercase") > -1) {
    outputText = inputText.toLowerCase();
  }

  if (layerName.indexOf("text-sentencecase") > -1) {
    outputText = textToSentenceCase(inputText);
  }

  if (layerName.indexOf("text-titlecase") > -1) {
    outputText = textToTitleCase(inputText);
  }

  // outputText = outputText.replace(/\s+/g, "\u00A0");

  return outputText;
};

const generateUniqueId = () => {
  return Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
  // Result: "k8z2n1l8n3k9z"
};

const loadFabricImage = async (source) => {
  if (source instanceof File) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (f) => {
        const img = await FabricImage.fromURL(f.target.result);
        resolve(img);
      };
      reader.readAsDataURL(source);
    });
  } else {
    // Treat as URL string
    return await FabricImage.fromURL(source, { crossOrigin: "anonymous" });
  }
};

/**
 * Normalizes a hex color string.
 * @param {string} color - The hex color string.
 * @returns {string|null} Normalized hex color in lowercase.
 */
function normalizeHexColor(color) {
  if (!color) return null;

  if (typeof color !== "string") {
    return color;
  }

  return color?.trim()?.toLowerCase();
}

let canvasInstance = null;

let designColorsFromTemplate = {
  text_primary: null,
  text_secondary: null,
  base_primary: null,
  base_secondary: null,
};

// let imageUrlsForTheDesign = [];
let usedImages = [];

/**
 * Extract image urls from the design data and save it to image url array
 * This is to preload the images before rendering the design on canvas
 */
function extractImageUrls(data) {
  // Regex to match common web image extensions (supports query strings)
  const imageRegex = /\.(jpg|jpeg|png|webp|gif|svg|bmp)(\?.*)?$/i;
  const imageUrls = new Set();

  // Helper to ensure the string is a valid remote HTTP/HTTPS image URL
  const isValidWebImageUrl = (str) =>
    typeof str === "string" &&
    (str.startsWith("http://") || str.startsWith("https://")) &&
    imageRegex.test(str);

  function traverse(val, key) {
    if (!val || typeof val !== "object") return;

    // // Rule: Skip co-agent images if co-agent is explicitly unavailable
    // if (
    //   key === "co_agent_profile_image_path" &&
    //   data?.is_co_agent_available === false
    // ) {
    //   return;
    // }

    // 1. Handle Arrays (e.g., design_images)
    if (Array.isArray(val)) {
      val.forEach((item) => traverse(item));
      return;
    }

    // 2. Handle Objects with `load === true`
    if (val?.load === true) {
      // Case A: Direct `url` property (e.g., in design_images or agency_logo)
      if (isValidWebImageUrl(val?.url)) {
        imageUrls.add(val?.url);
      }
      // Case B: Nested `image.url` property (for backward compatibility)
      else if (val?.image && isValidWebImageUrl(val?.image?.url)) {
        imageUrls.add(val?.image?.url);
      }
      // Case C: Fallback check for any valid string property in the object
      else {
        Object.values(val).forEach((item) => {
          if (isValidWebImageUrl(item)) {
            imageUrls.add(item);
          }
        });
      }
    } else {
      // 3. If load is not true, continue traversing nested properties
      Object.entries(val).forEach(([childKey, childVal]) => {
        traverse(childVal, childKey);
      });
    }
  }

  traverse(data);
  return Array.from(imageUrls);
}

/**
 * Checks if a layer name contains an EXACT tag, supporting spaces, underscores (_),
 * and string boundaries as separators.
 * * @param {string} layerName - The layer name string to test
 * @param {string} targetTag - The exact tag to look for (e.g., "agent-name")
 * @returns {boolean}
 */
function checkIfLayerNameMatches(layerName, targetTag) {
  if (!layerName || !targetTag) return false;

  var cleanName = String(layerName).replace(/^\s+|\s+$/g, "");
  var cleanTag = String(targetTag).replace(/^\s+|\s+$/g, "");

  if (cleanName === cleanTag) return true;

  // Escape special regex characters in targetTag
  var escapedTag = cleanTag.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");

  // Matches tag when preceded and followed by START/END (^/$), SPACES (\s), or UNDERSCORES (_)
  var regex = new RegExp("(?:^|[\\s_])" + escapedTag + "(?:$|[\\s_])");
  return regex.test(cleanName);
}

/**
 * Check if font layer is with italic fonts or not
 */
const isFontsItalic = (textObj) => {
  return textObj?.fontStyle === "italic";
};

async function loadSvgTemplate(url, reviver) {
  const response = await fetch(url);

  if (!response?.ok) {
    let message = `Failed to load SVG (${response.status})`;

    try {
      const error = await response.json();
      message = error.message || message;
    } catch {
      // Response wasn't JSON
    }

    return {
      success: false,
      result: null,
      error: {
        message,
      },
    };
  }

  const svg = await response.text();

  // debugger;

  return {
    success: true,
    result: await loadSVGFromString(svg, reviver),
    error: {
      message: "",
    },
  };
}

/**
 * Update bond price in the bond price text layer content
 *
 * */
function updateBondPrice(text, newPrice) {
  return text.replace(/\$\d+(?:,\d{3})*(?:\.\d+)?/, newPrice);
}

const ImageToolPostPreviewForAgency = ({
  imageData,
  setDesignCanvasInstance,
  canvasWidth = 1080,
  canvasHeight = 1080,
  previewCanvasWidth = 1080,
  previewCanvasHeight = 1080,
}) => {
  // console.log("imageData >> ", imageData);

  // console.log("reRenderDesign >>", reRenderDesign);
  // sampleDesignData.fonts = imageData.fonts;
  const designDimensions = useRef({
    canvas_width: canvasWidth,
    canvas_height: canvasHeight,
    preview_canvas_width: previewCanvasWidth,
    preview_canvas_height: previewCanvasHeight,
    artboard_width: 0,
    artboard_height: 0,
  });
  const agencyColors = useRef({
    text_primary: null,
    text_secondary: null,
    base_primary: null,
    base_secondary: null,
  }); // const previewImageRef = useRef(null);

  const [previewImageState, setPreviewImageState] = useState({
    image: null,
    showPreview: false,
  });

  // console.log("ImageToolPostPreviewForAgency rendered");

  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null); // 1080x1080

  const fileInputRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [sidebarKey, setSidebarKey] = useState(0);
  const [mobilePreviewImage, setMobilePreviewImage] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [commonState, setCommonState] = useState({
    loadingDesignTemplate: true,
    loadingDesignAssets: false,
    errorLoadingDesign: false,
  });

  console.log("commonState: ", commonState);

  // console.log("imageData.agency_logo.url >> ", imageData.agency_logo.url);

  let designTemplateUrl = imageData.template_url?.url;
  let imageUrlsForTheDesign = useRef([]);

  // console.log("designTemplateUrl", designTemplateUrl);
  // console.log("ImageToolPostPreviewForAgency > imageData: ", imageData);

  // let CANVAS_WIDTH = 720;
  // let CANVAS_HEIGHT = 1280;
  // let designDimensions.current.artboard_width = 1080;
  // let ARTBOARD_HEIGHT = 1920;

  /**
   * Need to set target scale manually for all design types (Width / Height independent)
   */
  // let getTargetScales().x = designDimensions.current.canvas_width / designDimensions.current.artboard_width;
  // let getTargetScales().y = CANVAS_HEIGHT / ARTBOARD_HEIGHT;

  const getTargetScales = () => {
    return {
      x:
        designDimensions.current.canvas_width /
        designDimensions.current.artboard_width,
      y:
        designDimensions.current.canvas_height /
        designDimensions.current.artboard_height,
    };
  };

  const getTargetScalesForPreview = () => {
    return {
      x:
        designDimensions.current.preview_canvas_width /
        designDimensions.current.artboard_width,
      y:
        designDimensions.current.preview_canvas_height /
        designDimensions.current.artboard_height,
    };
  };

  /**
   * Replaces a color with its corresponding agency color if it matches the template colors.
   * @returns {string} The replaced color or the original color if no match is found.
   */
  const replaceWithAgencyColors = ({ color, obj }) => {
    const normalizedColor = normalizeHexColor(color);
    const objId = obj?.id ?? "";

    /**
     * Sample ids with colors data
     * using-base-colors_color-base-primary_align-left_text-limit-22_width-480
     * color-text-primary_align-left_text-limit-22_width-480
     */

    let usingBaseColors = false;
    let usingTextColors = false;
    let disableColorUpdates = false;

    let baseColorPrimary = false;
    let baseColorSecondary = false;
    let textColorPrimary = false;
    let textColorSecondary = false;

    if (objId.includes("color-text-primary")) {
      textColorPrimary = true;
    }

    if (objId.includes("color-text-secondary")) {
      textColorSecondary = true;
    }

    if (objId.includes("color-base-primary")) {
      baseColorPrimary = true;
    }

    if (objId.includes("color-base-secondary")) {
      baseColorSecondary = true;
    }

    if (baseColorPrimary) {
      return agencyColors.current.base_primary;
    }

    if (baseColorSecondary) {
      return agencyColors.current.base_secondary;
    }

    if (textColorPrimary) {
      return agencyColors.current.text_primary;
    }

    if (textColorSecondary) {
      return agencyColors.current.text_secondary;
    }
  };

  /**
   * Set artboard width and height from the SVG options object
   */
  const setArtboardDimensions = (options) => {
    if (!options || typeof options !== "object") {
      // throw new Error("Invalid SVG options.");
    }

    const width = options?.viewBoxWidth ?? options?.width ?? 0;

    const height = options?.viewBoxHeight ?? options?.height ?? 0;

    designDimensions.current.artboard_width = width;
    designDimensions.current.artboard_height = height;

    // return {
    //   width,
    //   height,
    // };
  };

  let propertyStage = String(imageData.property_stage);
  let propertyAddress = String(imageData.property_address);
  let agencyLogoPath = String(imageData.agency_logo?.url);

  let agentName = String(imageData.agent_name);
  let agentEmail = String(imageData.agent_email);
  let agentPhone = String(imageData.agent_phone);
  let agentPhotoUrl = imageData.agent_profile_image_path?.url;

  let coAgentName = String(imageData.co_agent_name);
  let coAgentEmail = String(imageData.co_agent_email);
  let coAgentPhone = String(imageData.co_agent_phone);
  let coAgentPhotoUrl = imageData.co_agent_profile_image_path?.url;

  let noOfBedroom = String(imageData.bedroom_count);
  let noOfBathroom = String(imageData.bathroom_count);
  let noOfParking = String(imageData.parking_count);
  let noOfLiving = String(imageData.living_count);

  // let listingType = String(imageData.listing_type);

  let propertyArea = imageData.property_area?.replace(/²/g, "\u00B2");
  let openHomeDateTime = String(imageData.open_home_datetime);
  let primaryBaseColor = imageData.primary_base_color;
  let secondaryBaseColor = imageData.secondary_base_color;
  let primaryTextColor = imageData.primary_text_color;
  let secondaryTextColor = imageData.secondary_text_color;
  let designMainImage = imageData.design_main_image.url;
  let propertyPriceType = String(imageData.property_price_type);
  let startPrice = String(imageData.start_price);
  let endPrice = String(imageData.end_price);
  let propertyPrice = String(imageData.property_price);
  let eoiDateTime = String(imageData.eoi_datetime); // const isCoAgentAvailable = Boolean(imageData.is_co_agent_available);

  let dateTimeInfo = String(imageData?.datetime_info ?? "");
  let bondPrice = String(imageData?.bond_price ?? "");

  let designFontsData = imageData?.fonts ?? null;

  // if (!isCoAgentAvailable) {
  //   coAgentProfileImagePath = "";
  // }

  let propertyPriceString = propertyPrice;

  if (propertyPriceType === "price-range") {
    propertyPriceString = startPrice + "-" + endPrice;
  } else if (propertyPriceType === "price-per-week") {
    propertyPriceString = propertyPrice + " Per Week";
  } else if (propertyPriceType === "price-per-month") {
    propertyPriceString = propertyPrice + " Per Month";
  } else if (propertyPriceType === "contact-agent") {
    propertyPriceString = "Contact Agent";
  } else if (propertyPriceType === "offer-above-xyz-price") {
    propertyPriceString = "Offer Above - " + propertyPrice;
  } else if (propertyPriceType === "content-agent-for-sell") {
    propertyPriceString = "Contact Agent";
  } else if (propertyPriceType === "price-guide-xyz-price") {
    propertyPriceString = "Offer Above - " + propertyPrice;
  } else if (propertyPriceType === "eoi") {
    propertyPriceString = "EOI - " + eoiDateTime;
  }

  let agencyTradingName = "";
  let agencyWebsite = "";
  let officeContact = "";
  let officeEmail = "";
  let agencyAddress = "";

  if (imageData?.agency_trading_name) {
    agencyTradingName = imageData.agency_trading_name;
  }
  if (imageData?.agency_website) {
    agencyWebsite = imageData.agency_website;
  }
  if (imageData?.office_contact) {
    officeContact = imageData.office_contact;
  }
  if (imageData?.office_email) {
    officeEmail = imageData.office_email;
  }
  if (imageData?.agency_address) {
    agencyAddress = imageData.agency_address;
  }

  const saveHistory = (canv) => {
    const json = JSON.stringify(canv.toJSON());
    setHistory((prev) => [...prev.slice(0, historyIndex + 1), json]);
    setHistoryIndex((prev) => prev + 1);
  };

  const checkIfCoAgentAvailableOrNot = () => {
    // return false;
    if (
      coAgentPhotoUrl == "" ||
      !coAgentPhotoUrl ||
      !agentName ||
      agentName == ""
    )
      return false;
    return true;
  };

  // curl -I -H "Origin: http://localhost:3000" "https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/agency-logos/6a738bd00b4bac6b86b65c11/1785957329168-5310484c-d6d8-4583-af6c-fd5ae00f42bd.jpg"

  /**
   * 1. Helper: Preload an array of image URLs into a Map in parallel
   */
  const preloadAllImages = async (imageUrls) => {
    const uniqueUrls = [...new Set(imageUrls.filter(Boolean))];
    const imageMap = new Map();

    // console.log("uniqueUrls: ", uniqueUrls);

    const loadPromises = uniqueUrls.map(async (url) => {
      try {
        if (
          url ==
          "https://pub-0b7e31f75477417ca5d2b3de90ef25c1.r2.dev/agency-logos/6a475329b67b9fd13c35b129/1785991153069-795a2f81-bcae-4906-bad3-372c05f89443.png"
        ) {
          // let failedImage = await fetch(url, {
          //   mode: "cors",
          // });
          // debugger;
        }

        // const failedImage = new Image();
        // failedImage.crossOrigin = "anonymous";

        // failedImage.onload = () => console.log("loaded");
        // failedImage.onerror = (e) => console.error("failed", e);

        // failedImage.src = url;

        // let img = null;

        const element = new Image();
        element.crossOrigin = "anonymous";

        await new Promise((resolve, reject) => {
          element.onload = resolve;
          element.onerror = reject;
          // element.src = `${url}?v=${Date.now()}`;
          element.src = `${url}?v=1`;
          // element.src = `${url}`;
        });

        element.addEventListener("error", (err) => {
          // debugger;
        });

        // try {
        let img = new FabricImage(element);
        // } catch {
        //   img = await FabricImage.fromURL(url);
        // }
        // Fabric v6+ returns a Promise directly

        if (img && img._element) {
          imageMap.set(url, img);
        } else {
          console.warn(
            `[Image Preload Failed] Invalid image element from: ${url}`,
          );
        }
      } catch (error) {
        // debugger;
        // Catches network errors, CORS issues, or broken URLs
        console.warn(
          `[Image Preload Failed] Could not load image from: ${url}`,
          error,
        );
      }
    });

    await Promise.all(loadPromises);
    return imageMap; // Returns Map: url -> FabricImage
  };

  const extractTransformCoordinates = (transformString) => {
    // Regex explanation:
    // translate\( -> matches the literal text "translate("
    // ([-\d.]+)  -> 1st Group: matches digits, dots, or minus signs (positionX)
    // \s+        -> matches one or more spaces or a comma
    // ([-\d.]+)  -> 2nd Group: matches digits, dots, or minus signs (positionY)
    const regex = /translate\(([-\d.]+)\s+([-\d.]+)\)/;
    const match = transformString?.match(regex);

    if (match) {
      const positionX = parseFloat(match[1]);
      const positionY = parseFloat(match[2]);

      // console.log("positionX:", positionX); // 482.11
      // console.log("positionY:", positionY); // 291.78

      return { positionX, positionY };
    } else {
      console.error("No valid translate coordinates found.");
      return null;
    }
  };

  const processTextContent = (textLayer) => {
    try {
      const textLayerName = textLayer?.layer_name_from_source ?? textLayer?.id;
      const updatedName = textLayerName.toLowerCase();

      // Property Address
      if (
        propertyAddress &&
        checkIfLayerNameMatches(updatedName, "property-address")
      ) {
        let _propertyAddress = validateAndTransformTextCase(
          updatedName,
          propertyAddress,
        );
        textLayer.set({ text: _propertyAddress });
      }

      // Property Stage
      if (
        propertyStage &&
        checkIfLayerNameMatches(updatedName, "property-stage")
      ) {
        let _propertyStage = validateAndTransformTextCase(
          updatedName,
          propertyStage,
        );
        textLayer.set({ text: _propertyStage });
      }

      // Property Area
      if (
        propertyArea &&
        checkIfLayerNameMatches(updatedName, "property-area")
      ) {
        let _propertyArea = validateAndTransformTextCase(
          updatedName,
          propertyArea,
        );
        textLayer.set({ text: _propertyArea });
      }

      // Property Price
      if (
        propertyPriceString &&
        checkIfLayerNameMatches(updatedName, "property-price")
      ) {
        let _propertyPriceString = validateAndTransformTextCase(
          updatedName,
          propertyPriceString,
        );
        textLayer.set({ text: _propertyPriceString });
      }

      // Office Contact
      if (
        officeContact &&
        checkIfLayerNameMatches(updatedName, "office-contact")
      ) {
        let _officeContact = validateAndTransformTextCase(
          updatedName,
          officeContact,
        );
        textLayer.set({ text: _officeContact });
      }

      // Office Email
      if (officeEmail && checkIfLayerNameMatches(updatedName, "office-email")) {
        let _officeEmail = validateAndTransformTextCase(
          updatedName,
          officeEmail,
        );
        textLayer.set({ text: _officeEmail });
      }

      // Agency Website
      if (
        agencyWebsite &&
        checkIfLayerNameMatches(updatedName, "agency-website")
      ) {
        let _agencyWebsite = validateAndTransformTextCase(
          updatedName,
          agencyWebsite,
        );
        textLayer.set({ text: _agencyWebsite });
      }

      // Agency Address
      if (
        agencyAddress &&
        checkIfLayerNameMatches(updatedName, "agency-address")
      ) {
        let _agencyAddress = validateAndTransformTextCase(
          updatedName,
          agencyAddress,
        );
        textLayer.set({ text: _agencyAddress });
      }

      // Bathroom Count
      if (noOfBathroom && checkIfLayerNameMatches(updatedName, "bath-count")) {
        let _noOfBathroom = validateAndTransformTextCase(
          updatedName,
          noOfBathroom,
        );
        textLayer.set({ text: _noOfBathroom });
      }

      // Bedroom Count
      if (noOfBedroom && checkIfLayerNameMatches(updatedName, "bed-count")) {
        let _noOfBedroom = validateAndTransformTextCase(
          updatedName,
          noOfBedroom,
        );
        textLayer.set({ text: _noOfBedroom });
      }

      // Parking Count
      if (
        noOfParking &&
        checkIfLayerNameMatches(updatedName, "parking-count")
      ) {
        let _noOfParking = validateAndTransformTextCase(
          updatedName,
          noOfParking,
        );
        textLayer.set({ text: _noOfParking });
      }

      // Property Area
      if (
        propertyArea &&
        checkIfLayerNameMatches(updatedName, "property-area")
      ) {
        let _propertyArea = validateAndTransformTextCase(
          updatedName,
          propertyArea,
        );
        textLayer.set({ text: _propertyArea });
      }

      // Living Count
      if (noOfLiving && checkIfLayerNameMatches(updatedName, "living-count")) {
        let _noOfLiving = validateAndTransformTextCase(updatedName, noOfLiving);
        textLayer.set({ text: _noOfLiving });
      }

      // Agency Trading Name
      if (
        agencyTradingName &&
        checkIfLayerNameMatches(updatedName, "agency-trading-name")
      ) {
        let _agencyTradingName = validateAndTransformTextCase(
          updatedName,
          agencyTradingName,
        );
        textLayer.set({ text: _agencyTradingName });
      }

      // Agent info
      if (
        dateTimeInfo &&
        checkIfLayerNameMatches(updatedName, "datetime-info")
      ) {
        textLayer.set({ text: dateTimeInfo });
      }

      if (bondPrice && checkIfLayerNameMatches(updatedName, "property-bond")) {
        let bodPriceText = updateBondPrice(textLayer.text, bondPrice);

        textLayer.set({ text: bodPriceText });
      }

      // Agent info
      if (agentPhone && checkIfLayerNameMatches(updatedName, "agent-phone")) {
        textLayer.set({ text: formatPhoneNumber(agentPhone) });
      }

      if (agentName && checkIfLayerNameMatches(updatedName, "agent-name")) {
        let _agentName = validateAndTransformTextCase(updatedName, agentName);
        textLayer.set({ text: _agentName });
      }

      if (agentEmail && checkIfLayerNameMatches(updatedName, "agent-email")) {
        let updatedText = validateAndTransformTextCase(updatedName, agentEmail);
        textLayer.set({ text: updatedText });
      }

      // Co-Agent info
      if (checkIfLayerNameMatches(updatedName, "co-agent-phone")) {
        if (coAgentPhone && coAgentPhone !== "") {
          textLayer.set({ text: formatPhoneNumber(coAgentPhone) });
        } else {
          // layersToBeDeleted.push(textLayer);
        }
      }

      if (checkIfLayerNameMatches(updatedName, "co-agent-name")) {
        if (coAgentName && coAgentName !== "") {
          let _coAgentName = validateAndTransformTextCase(
            updatedName,
            coAgentName,
          );
          textLayer.set({ text: _coAgentName });
        } else {
          // layersToBeDeleted.push(textLayer);
        }
      }

      if (
        coAgentEmail &&
        checkIfLayerNameMatches(updatedName, "co-agent-email")
      ) {
        let updatedText = validateAndTransformTextCase(
          updatedName,
          coAgentEmail,
        );
        textLayer.set({ text: updatedText });
      }
    } catch (e) {
      // Error handling
    }
  };

  const loadAndRenderDesignOnCanvas = async () => {
    try {
      if (!canvas) return;

      console.log("rendering design >>>");

      setCommonState((prev) => {
        return {
          ...prev,
          loadingDesignTemplate: true,
          loadingDesignAssets: false,
        };
      });

      /**
       * Load the svg template and render it on the canvas
       */
      const { success, error, result } = await loadSvgTemplate(
        designTemplateUrl,
        function reviver(elem, obj) {
          // This runs for EVERY parsed element (text, path, rect, tspan, etc.)
          let layerName = obj?.id ?? "";
          layerName = layerName?.toLowerCase();
          // console.log("layerName >>", layerName);

          if (obj.type === "text" || obj.type === "textbox") {
            // Extract width from your naming convention (e.g., "width-600")
            const match = layerName.match(/width-([^\s_<>]+)/i);
            let desiredWidth = match ? Number(match[1]) : null;

            if (desiredWidth) {
              obj._isAreaText = true;
            }
          }

          const transformAttr = elem.getAttribute("transform");
          const dataNameAttr = elem.getAttribute("data-name");
          obj.set("originalTransformAttributes", transformAttr);
          obj.set("layer_name_from_source", dataNameAttr);

          /**
           * Get colors metadata from colors metadata layer and assign to global variables
           */
          if (dataNameAttr?.includes("colors-data")) {
            const getColorFromMetadata = (key) => {
              const match = dataNameAttr.match(new RegExp(`${key}=([^\\s]+)`));
              return match ? normalizeHexColor(`#${match[1]}`) : null;
            };

            designColorsFromTemplate.text_primary =
              getColorFromMetadata("text_primary");
            designColorsFromTemplate.text_secondary =
              getColorFromMetadata("text_secondary");
            designColorsFromTemplate.base_primary =
              getColorFromMetadata("base_primary");
            designColorsFromTemplate.base_secondary =
              getColorFromMetadata("base_secondary");

            // console.log(
            //   "designColorsFromTemplate >>",
            //   designColorsFromTemplate,
            // );
          }
        },
      );

      if (!success) {
        console.error("Something went wrong loading the design template");
      }

      setCommonState((prev) => {
        return {
          ...prev,
          loadingDesignTemplate: false,
          loadingDesignAssets: true,
        };
      });

      // PRELOAD PHASE (Parallel fetch before rendering)
      // Show loading UI here if desired: setLoading(true)
      const preloadedImagesMap = await preloadAllImages(
        imageUrlsForTheDesign.current,
      );

      canvas.clear();
      // const validObjects = objects.filter((o) => o !== null);

      const { objects, options } = result;

      setArtboardDimensions(options);

      const validObjects = [];

      for (let obj of objects) {
        if (!obj) return false;

        let layerName = (
          obj?.id ||
          obj?.layer_name_from_source ||
          ""
        ).toLowerCase();

        const isCoAgentAvailable = checkIfCoAgentAvailableOrNot();
        // console.log("isCoAgentAvailable", isCoAgentAvailable);

        // If co-agent is not available, exclude all co-agent layers from canvas creation
        if (isCoAgentAvailable) {
          // Don't return layers for single agent info if co-agent is available

          if (layerName?.includes("single-agent-info")) {
            continue; // Drops the object completely
          }
        } else {
          if (layerName?.includes("multiple-agent-info")) {
            // console.log("Skipping layer: ", layerName);
            continue; // Drops the object completely
          }
        }

        validObjects.push(obj);
      }

      validObjects.forEach((obj) => {
        const raw = obj?.toObject();
        let layerName = (
          obj?.id ||
          obj?.layer_name_from_source ||
          ""
        ).toLowerCase();

        if (
          obj.type.includes("text") ||
          obj.type.includes("textbox") ||
          obj.type === "textbox"
        ) {
          layerName = layerName.toLowerCase();

          let isAreaText = obj?._isAreaText || false;

          const match = layerName.match(/width-([^\s_<>]+)/i);
          let desiredWidth = match ? Number(match[1]) : null;

          const alignMatch = layerName.match(/align-([^\s_<>]+)/i);
          const objectAlignment = alignMatch
            ? alignMatch[1]
            : CommonEnums.alignment.left;

          const charLimitMatch = layerName.match(/text-limit-(\d+)/i);
          const characterLimit = charLimitMatch
            ? Number(charLimitMatch[1])
            : null;

          obj.set("textLimit", characterLimit);

          const coords = extractTransformCoordinates(
            obj?.originalTransformAttributes,
          );

          if (objectAlignment) {
            obj.set("objectAlignment", objectAlignment);
          }

          if (isAreaText) {
            if (coords) {
              obj.set({
                left: coords.positionX * getTargetScales().x,
                top: coords.positionY * getTargetScales().y,
                absolutePositioned: true,
                originX: "left",
                originY: "top",
                width: desiredWidth,
                textAreaWidth: desiredWidth,
                _positionX: coords.positionX,
                _positionY: coords.positionY,
                splitByGrapheme: false,
              });
            }
          }

          obj.setCoords();
        }
      });

      // Don't group the SVG.
      // Keep every object in Illustrator's original coordinate system.

      const children = validObjects;

      // Scale every object individually.
      children.forEach((obj) => {
        obj.set({
          left: obj.left * getTargetScales().x,
          top: obj.top * getTargetScales().y,
          scaleX: obj.scaleX * getTargetScales().x,
          scaleY: obj.scaleY * getTargetScales().y,
        });

        obj.setCoords();
      });

      // Define Image Renderer helper function that works sequentially
      const renderImageObject = async (obj, allChildren) => {
        const imageObj = obj;
        let mask = imageObj.clipPath;
        let imageLayerName = imageObj?.id;
        let isLogoImage = imageLayerName?.includes("agency-logo-image");

        // console.log("imageLayerName: ", imageLayerName);

        if (mask) {
          let placeholder = null;
          const cp = obj.clipPath;
          let imageGroupId = generateUniqueId();

          const commonProps = {
            left: obj.left + cp.left * obj.scaleX,
            top: obj.top + cp.top * obj.scaleY,
            scaleX: cp.scaleX * obj.scaleX,
            scaleY: cp.scaleY * obj.scaleY,
            isPlaceholderFrame: true,
            fill: "transparent",
            strokeWidth: 0,
            imageGroupId: imageGroupId,
            selectable: false,
            evented: false,
          };

          if (cp.type === CommonEnums.shapeTypes.rect) {
            placeholder = new Rect({
              ...commonProps,
              width: cp.width,
              height: cp.height,
            });
          } else if (cp.type === CommonEnums.shapeTypes.circle) {
            placeholder = new Circle({
              ...commonProps,
              radius: cp.radius,
            });
          } else if (cp.type === CommonEnums.shapeTypes.ellipse) {
            placeholder = new Ellipse({
              ...commonProps,
              rx: cp.rx,
              ry: cp.ry,
            });
          } else if (cp.type === CommonEnums.shapeTypes.polygon) {
            placeholder = new Polygon(cp.points, {
              ...commonProps,
            });
          } else if (cp.type === CommonEnums.shapeTypes.path) {
            placeholder = new Path(cp.path, {
              ...commonProps,
              fillRule: "evenodd",
            });
          }

          if (placeholder) {
            canvas.add(placeholder);
            placeholder.setCoords();
          }

          imageObj.set({
            clipPath: mask,
            selectable: true,
            hasControls: true,
            hoverCursor: "pointer",
            clipPathOriginalWidth: mask.width,
            clipPathOriginalHeight: mask.height,
            imageGroupId: imageGroupId,
            visible: false,
          });

          canvas.add(imageObj);

          let newImg = null;
          let designImageUrl = "";

          const layerNameParts = imageLayerName
            ?.toLowerCase()
            .split(/[^a-z0-9-]+/)
            .filter(Boolean);

          // console.log("imageLayerName: ", imageLayerName);

          if (layerNameParts) {
            const isAgentImage = layerNameParts?.includes("agent-photo");
            const isCoAgentImage = layerNameParts?.includes("co-agent-photo");

            if (isAgentImage) {
              designImageUrl = agentPhotoUrl;
            }

            if (isCoAgentImage) {
              designImageUrl = coAgentPhotoUrl;
            }

            if (layerNameParts.includes("main-image"))
              designImageUrl = designMainImage;
          }

          if (imageLayerName.includes("design-image")) {
            let designImages = imageData.design_images;

            // console.log("designImages: ", designImages);

            for (let idx = 0; idx < designImages.length; idx++) {
              const propertyImage = designImages[idx];
              let imageIndex = propertyImage.index;

              // console.log("imageIndex: ", imageIndex);

              if (propertyImage.image_type == "property_image") {
                if (!usedImages.includes(imageIndex)) {
                  designImageUrl = propertyImage?.url;
                  usedImages.push(imageIndex);
                  break;
                }
              }

              // if (propertyImage.image_type == "property_image") {
              //   if (!usedImages.includes(imageIndex)) {
              //     designImageUrl = propertyImage?.url;
              //     usedImages.push(imageIndex);
              //     break;
              //   }
              // }
            }
          }

          const preloadedImg = preloadedImagesMap.get(designImageUrl);

          if (preloadedImg) newImg = preloadedImg;
          else newImg = await loadFabricImage(designImageUrl);

          let clipPathOriginalWidth =
            imageObj.clipPathOriginalWidth * getTargetScales().x;
          let clipPathOriginalHeight =
            imageObj.clipPathOriginalHeight * getTargetScales().y;

          const targetWidth = clipPathOriginalWidth;
          const targetHeight = clipPathOriginalHeight;

          const scale = util.findScaleToCover(
            {
              width: newImg.width,
              height: newImg.height,
            },
            { width: targetWidth, height: targetHeight },
          );

          // let imageGroupId = selectedObject?.imageGroupId;

          let targetShape = placeholder;

          // const allObjects = canvas.getObjects();

          // for (let _idx = 0; _idx < allObjects.length; _idx++) {
          //   const _obj = allObjects[_idx];

          //   if (_obj?.imageGroupId == imageGroupId) {
          //     targetShape = _obj;
          //     break;
          //   }
          // }

          const targetShapeCenter = targetShape.getCenterPoint();

          newImg.set({
            left: targetShapeCenter.x,
            top: targetShapeCenter.y,
            scaleX: scale,
            scaleY: scale,
            originX: "center",
            originY: "center",
            angle: imageObj.angle, // Keep the original rotation
            clipPathOriginalWidth: imageObj.clipPathOriginalWidth,
            clipPathOriginalHeight: imageObj.clipPathOriginalHeight,
            layer_name_from_source: imageObj?.layer_name_from_source,
            imageGroupId: imageGroupId,
            selectable: false,
            evented: false,
          });

          // 4. THE FIX: Transfer the clipPath without using .clone()
          if (imageObj?.clipPath) {
            const imageMask = await targetShape.clone();

            // 2. Configure the imageMask to be the "Static Window"
            imageMask.set({
              absolutePositioned: true, // CRITICAL: Keeps imageMask locked to 800x800 canvas coords
              originX: "center",
              originY: "center",
              left: targetShape.getCenterPoint().x,
              top: targetShape.getCenterPoint().y,
              imageGroupId: imageGroupId,
              selectable: false,
              evented: false,
            });

            newImg.set({ clipPath: imageMask });
          }

          // 5. Swap the objects in the canvas
          const currentIndex = canvas.getObjects().indexOf(imageObj);
          canvas.add(newImg);

          if (currentIndex !== -1) {
            canvas.moveObjectTo(newImg, currentIndex);
            canvas.remove(imageObj);
          }
          newImg.setCoords();

          return;
        }

        if (isLogoImage) {
          const lock = {
            lockMovementX: false,
            lockMovementY: false,
            lockScalingX: false,
            lockScalingY: false,
            lockRotation: false,
            hasControls: false,
            selectable: false,
            evented: false,
          };

          imageObj.set({ ...lock, hoverCursor: "default", visible: false });

          // Search for logo placeholder shape from children array
          const logoPlaceholderShape = allChildren.find((_child) =>
            _child?.layer_name_from_source?.includes("agency-logo-shape"),
          );

          if (logoPlaceholderShape) {
            const preloadedImg = preloadedImagesMap.get(agencyLogoPath);

            let newImg = null;
            if (preloadedImg) newImg = preloadedImg;
            else newImg = await loadFabricImage(agencyLogoPath);

            const padding = 1;
            const limitW = logoPlaceholderShape.getScaledWidth() - padding * 2;
            const limitH = logoPlaceholderShape.getScaledHeight() - padding * 2;

            const fitScale = util.findScaleToFit(
              { width: newImg.width, height: newImg.height },
              { width: limitW, height: limitH },
            );

            const anchorCenter = logoPlaceholderShape.getCenterPoint();

            newImg.set({
              left: anchorCenter.x,
              top: anchorCenter.y,
              scaleX: fitScale,
              scaleY: fitScale,
              originX: "center",
              originY: "center",
              angle: logoPlaceholderShape.angle,
              layer_name_from_source: imageObj?.layer_name_from_source,
              selectable: false,
              evented: false,
            });

            const rectCenter = logoPlaceholderShape.getCenterPoint();
            const rectWidth = logoPlaceholderShape.getScaledWidth();

            let alignmentCmd = CommonEnums.alignment.left;
            const alignmentRegex = /align-([^\s_<>]+)/;
            const alignmentMatch = imageLayerName.match(alignmentRegex);

            if (alignmentMatch) {
              alignmentCmd = alignmentMatch[1];
            }

            const absoluteLeft = rectCenter.x - rectWidth / 2;
            const absoluteRight = rectCenter.x + rectWidth / 2;

            if (alignmentCmd === CommonEnums.alignment.left) {
              newImg.set({
                left: absoluteLeft + padding,
                originX: "left",
              });
            } else if (alignmentCmd === CommonEnums.alignment.right) {
              newImg.set({
                left: absoluteRight - padding,
                originX: "right",
              });
            } else if (alignmentCmd === CommonEnums.alignment.center) {
              newImg.set({
                left: rectCenter.x,
                originX: "center",
              });
            }

            canvas.add(newImg);
            newImg.setCoords();
          } else {
            canvas.add(imageObj);
          }

          return;
        }

        if (!mask) {
          obj.set({ selectable: true, hasControls: true });
          canvas.add(obj);
        }
      };

      // SEQUENTIAL RENDERING LOOP TO PRESERVE ORIGINAL Z-INDEX
      for (const obj of children) {
        let disableColorUpdates = false;

        // console.log("Layer name: ", obj?.id);

        if (obj?.id == "agent-photo") {
          // debugger;
        }

        if (obj?.id?.includes("lock-color")) {
          disableColorUpdates = true;
        }

        if (!disableColorUpdates) {
          if (obj?.fill) {
            obj.set("fill", replaceWithAgencyColors({ color: obj.fill, obj }));
          }
          if (obj?.stroke) {
            obj.set(
              "stroke",
              replaceWithAgencyColors({ color: obj.stroke, obj }),
            );
          }
          if (obj?.textBackgroundColor) {
            obj.set(
              "textBackgroundColor",
              replaceWithAgencyColors({
                color: obj.textBackgroundColor,
                obj,
              }),
            );
          }
          if (obj?.backgroundColor) {
            obj.set(
              "backgroundColor",
              replaceWithAgencyColors({ color: obj.backgroundColor, obj }),
            );
          }
        }

        const lock = {
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
          hasControls: true,
          selectable: true,
          evented: false,
        };

        if (
          obj.type.includes("text") ||
          obj.type.includes("textbox") ||
          obj.type === "tspan" ||
          obj.type === "textbox"
        ) {
          const raw = obj.toObject();
          let isAreaText = obj?._isAreaText || false;
          let textAlignment = obj?.objectAlignment;
          let textLimit = obj?.textLimit;
          let textAreaWidth = obj?.textAreaWidth;
          let layerName = (
            obj?.id ||
            obj?.layer_name_from_source ||
            ""
          ).toLowerCase();

          delete raw.type;

          let textContent = null;

          let extraProps = {
            textLimit,
            textAlignment,
            originalLeftAlignment: obj.left,
            id: obj.id || raw.id || "text",
          };

          /**
           * 400 (regular) font weight is not included in the exported svg from illustrator designs
           * So we are keeping 400 as default font weight
           */
          let fontWeight = obj?.fontWeight || raw?.fontWeight || 400;

          if (fontWeight === "normal") fontWeight = 400;

          let fontStyle = obj?.fontStyle || "normal";

          let isTitleText = checkIfLayerNameMatches(layerName, "title-text");
          let isBodyText = checkIfLayerNameMatches(layerName, "body-text");
          let isSubtitleText = checkIfLayerNameMatches(
            layerName,
            "subtitle-text",
          );

          let isItalic = isFontsItalic(obj);

          let font_data_field = `font_${fontWeight}`;

          if (isItalic) {
            font_data_field = `font_${fontWeight}_italic`;
          }

          let updatedFontFamily = raw?.fontFamily;

          // debugger;

          if (isTitleText) {
            if (
              designFontsData?.title?.[font_data_field].postscript_name &&
              designFontsData?.title?.[font_data_field].loaded
            ) {
              updatedFontFamily =
                designFontsData?.title?.[font_data_field].postscript_name;
            }
          }

          if (isSubtitleText) {
            if (
              designFontsData?.subtitle?.[font_data_field].postscript_name &&
              designFontsData?.subtitle?.[font_data_field].loaded
            ) {
              updatedFontFamily =
                designFontsData?.subtitle?.[font_data_field].postscript_name;
            }
          }

          if (isBodyText) {
            if (
              designFontsData?.body?.[font_data_field].postscript_name &&
              designFontsData?.body?.[font_data_field].loaded
            ) {
              updatedFontFamily =
                designFontsData?.body?.[font_data_field].postscript_name;
            }
          }

          if (!isAreaText) {
            textContent = new IText(obj.text || "Edit", {
              ...raw,
              ...lock,
              padding: 0,
              hoverCursor: "pointer",
              fontFamily: updatedFontFamily,
              fontSize: raw.fontSize || 40,
              left: obj.left,
              top: obj.top,
              ...extraProps,
            });
          } else {
            textContent = new Textbox(obj.text || "Edit", {
              ...raw,
              ...lock,
              fontFamily: updatedFontFamily,
              hoverCursor: "pointer",
              fontSize: raw.fontSize || 40,
              ...extraProps,
            });
          }

          if (!textContent) continue;

          processTextContent(textContent);

          textContent.initDimensions();
          textContent.setCoords();

          if (isAreaText) {
            textContent.setCoords();
          }

          const originalLeft = obj.getBoundingRect().left;
          let originalRight = originalLeft + obj.getBoundingRect().width;
          const centerX = originalLeft + obj.getBoundingRect().width / 2;
          let objId = obj?.id;

          const positionFromLeftMatch = objId.match(
            /center-position-from-left-(\d+)/,
          );
          const positionFromTopMatch = objId.match(/position-from-top-(\d+)/);

          let positionFromTop = Number(positionFromTopMatch?.[1]) || 0;
          let centerPositionFromLeft = Number(positionFromLeftMatch?.[1]) || 0;

          if (textAlignment === CommonEnums.alignment.left) {
            if (isAreaText) {
              let _originalLeft = obj._positionX * getTargetScales().x;
              // let _originalTop =
              //   (obj._positionY - obj.height * getTargetScales().y) *
              //   getTargetScales().y;

              let updatedTop = positionFromTop * getTargetScales().y;

              textContent.set({
                width: textAreaWidth,
                originX: "left",
                textAlign: "left",
                left: _originalLeft,
                top: updatedTop,
              });
            } else {
              textContent.set({
                originX: "left",
                textAlign: "left",
                left: originalLeft,
              });
            }
          }

          if (textAlignment === CommonEnums.alignment.center) {
            if (isAreaText) {
              let updatedTop = positionFromTop * getTargetScales().y;

              textContent.set({
                originX: "center",
                originY: "top",
                textAlign: "center",
                left: centerPositionFromLeft * getTargetScales().x,
                top: updatedTop,
              });
            } else {
              textContent.set({
                originX: "center",
                textAlign: "center",
                left: centerX,
              });
            }
          }

          if (textAlignment === CommonEnums.alignment.right) {
            let slotWidth = obj.width * obj.scaleX;

            let rightParams = {
              width: slotWidth,
              splitByGrapheme: false,
            };

            let updatedTop = positionFromTop * getTargetScales().y;

            if (isAreaText) {
              rightParams.width = textAreaWidth;
              let _originalTop =
                (obj._positionY - obj.height * getTargetScales().y) *
                getTargetScales().y;

              textContent.set({
                ...rightParams,
              });

              const positionFromRightMatch = objId.match(
                /position-from-right-(\d+)/,
              );
              let positionFromRight = Number(positionFromRightMatch[1]) || 0;

              textContent.initDimensions();

              textContent.set({
                originX: "right",
                textAlign: "right",
                left:
                  designDimensions.current.artboard_width *
                    getTargetScales().x -
                  positionFromRight * getTargetScales().x,
                top: updatedTop,
              });
            } else {
              textContent.set({
                originX: "right",
                textAlign: "right",
                left: originalRight,
              });
            }

            textContent.initDimensions();
            textContent.setCoords();
          }

          textContent.setCoords();
          canvas.add(textContent);
        } else if (obj.type == "image") {
          // Await image rendering directly in loop so it maintains exact index order
          await renderImageObject(obj, children);
        } else {
          obj.set({ ...lock, hoverCursor: "default" });
          canvas.add(obj);
        }

        obj.setCoords();
      }

      canvas.renderAll();

      setCommonState((prev) => {
        return {
          ...prev,
          loadingDesignTemplate: false,
          loadingDesignAssets: false,
        };
      });
    } catch (err) {
      // debugger
      console.error("SVG Import Error:", err);

      setCommonState((prev) => {
        return {
          ...prev,
          loadingDesignTemplate: false,
          loadingDesignAssets: false,
          errorLoadingDesign: true,
        };
      });
    }
  };

  const extractAgencyColors = () => {
    /**
     * Assign agency design colors to global variables
     */
    agencyColors.current.text_primary = normalizeHexColor(
      imageData?.text_color_primary.hex,
    );
    agencyColors.current.text_secondary = normalizeHexColor(
      imageData?.text_color_secondary.hex,
    );
    agencyColors.current.base_primary = normalizeHexColor(
      imageData?.base_color_primary.hex,
    );
    agencyColors.current.base_secondary = normalizeHexColor(
      imageData?.base_color_secondary.hex,
    );
  };

  useEffect(() => {
    // console.log("agencyColors >>>", agencyColors);

    /**
     * Extract image urls from the design data and save it to image url array
     * This is to preload the images before rendering the design on canvas
     */
    imageUrlsForTheDesign.current = extractImageUrls(imageData);
    const isMobile = window.innerWidth <= 1024;

    // console.log("imageUrlsForTheDesign >>>", imageUrlsForTheDesign);

    const fabricCanvas = new Canvas(canvasRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: "#ffffff",
      // enableRetinaScaling: false,
      allowTouchScrolling: true,
    });

    // const previewCanvas = new Canvas(previewCanvasRef.current, {
    //   width: 1080,
    //   height: 1080,
    //   backgroundColor: "#ffffff",
    //   // enableRetinaScaling: false,
    // });

    /**
     * Disable select events for the canvas
     */
    fabricCanvas.selection = false;
    // previewCanvas.selection = false;

    if (isMobile) {
      fabricCanvas.skipTargetFind = true;
      fabricCanvas.interactive = false;
      fabricCanvas.selection = false;
    }

    const forceScrollableCanvas = () => {
      const wrapper = canvasRef.current?.parentElement;
      if (!wrapper) return;

      const allCanvases = wrapper.querySelectorAll("canvas");
      allCanvases.forEach((c) => {
        c.style.touchAction = "pan-y";
        if (isMobile) {
          c.style.pointerEvents = "none"; // Mobile pe events completely block
        }
      });

      wrapper.style.touchAction = "pan-y";
      if (isMobile) {
        wrapper.style.pointerEvents = "none";
      }
    };

    forceScrollableCanvas();
    setTimeout(forceScrollableCanvas, 100);
    setTimeout(forceScrollableCanvas, 500);
    setTimeout(forceScrollableCanvas, 1500);

    canvasInstance = fabricCanvas;

    // --- HOVER VISUALS ---
    fabricCanvas.on("mouseover", (e) => {
      const obj = e.target;
      if (obj && (obj instanceof IText || obj.type === "image")) {
        obj.set({
          stroke: "#3b82f6",
          strokeWidth: 2,
          strokeDashArray: [6, 4],
          strokeUniform: true,
          dirty: true,
        });
        fabricCanvas.requestRenderAll();
      }
    });

    fabricCanvas.on("mouseout", (e) => {
      const obj = e.target;
      if (obj && (obj instanceof IText || obj.type === "image")) {
        obj.set({ stroke: null, strokeWidth: 0, dirty: true });
        fabricCanvas.requestRenderAll();
      }
    });

    // --- SELECTION SYNC ---
    const handleSelection = (e) => {
      setSelectedObject(e.selected ? e.selected[0] : null);
      setSidebarKey((prev) => prev + 1);
    };
    fabricCanvas.on("selection:created", handleSelection);
    fabricCanvas.on("selection:updated", handleSelection);
    fabricCanvas.on("selection:cleared", () => setSelectedObject(null));

    fabricCanvas.on("object:modified", () => saveHistory(fabricCanvas));
    // fabricCanvas.on("text:changed", () => saveHistory(fabricCanvas));

    fabricCanvas.on("text:changed", (e) => {
      const obj = e.target;

      // console.log("obj.text >>>", obj.text);
      if (!obj || !obj?.textLimit) return;

      let textLimit = obj?.textLimit;
      let textLength = obj.text.length;
      let leftAlignment = obj.originalLeftAlignment;
      let currentLeft = obj.left;
      let currentTop = obj.top;
      // console.log("textLimit >>>", textLimit);
      // console.log("textLength >>>", textLength);

      let safeTextValues = safeTextInputs?.[obj.id];

      // Use the custom key 'textLimit' we set during import
      if (textLength > textLimit) {
        // console.log("obj.left >>>", obj.left);
        // console.log("leftAlignment >>>", leftAlignment);
        // Truncate safely
        const truncatedText = safeTextValues.text.substring(0, textLimit);

        // Sync the hidden input buffer to prevent disappearance/ghosting in v6
        if (obj.hiddenTextarea) {
          obj.hiddenTextarea.value = truncatedText;
        }

        obj.selectionStart = obj.textLimit;
        obj.selectionEnd = obj.textLimit;

        obj.set({
          text: safeTextValues.text,
          left: safeTextValues.left,
          top: safeTextValues.top,
          dirty: true,
        });
        obj.setCoords();
        fabricCanvas.requestRenderAll();
      } else {
        safeTextInputs[obj.id] = {
          text: obj.text,
          left: obj.left,
          top: obj.top,
        };
      }

      saveHistory(fabricCanvas);
    });

    setCanvas(fabricCanvas);
    setDesignCanvasInstance(fabricCanvas);

    return () => {
      fabricCanvas.clear();
      fabricCanvas.dispose();
    };
  }, [imageData]);

  //   console.log("selectedObject.width >>>", selectedObject.width);
  // console.log("selectedObject.height >>>", selectedObject.height);

  // console.log("selectedObject.scaleX >>>", selectedObject.scaleX);
  // console.log("selectedObject.scaleY >>>", selectedObject.scaleY);

  // console.log("targetWidth >>>", targetWidth);
  // console.log("targetHeight >>>", targetHeight);

  const loadFabricImage = async (source) => {
    if (source instanceof File) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (f) => {
          const img = await FabricImage.fromURL(f.target.result, {
            crossOrigin: "anonymous",
          });
          resolve(img);
        };
        reader.readAsDataURL(source);
      });
    } else {
      // Treat as URL string
      return await FabricImage.fromURL(source, { crossOrigin: "anonymous" });
    }
  };

  const processImageReplacement = async (file) => {
    if (!file || !selectedObject || !canvas) return;

    const reader = new FileReader();
    reader.onload = async (f) => {
      const data = f.target.result;
      try {
        // 1. Load the new image
        const newImg = await loadFabricImage(file);
        let selectedLayerName = selectedObject?.layer_name_from_source;

        // get all objects
        const allObjects = canvas.getObjects();

        let isLogoImage = false;
        if (selectedLayerName?.includes("agency-logo-image")) {
          isLogoImage = true;
        }

        if (isLogoImage) {
          let logoPlaceholderShape = null;

          for (let _idx = 0; _idx < allObjects.length; _idx++) {
            const _obj = allObjects[_idx];

            if (!_obj?.layer_name_from_source) continue;

            if (_obj?.layer_name_from_source?.includes("agency-logo-shape")) {
              logoPlaceholderShape = _obj;
              break;
            }
          }

          const padding = 0; // Internal padding for safe replacement of logo within logo placeholder shape
          const limitW = logoPlaceholderShape.getScaledWidth() - padding * 2;
          const limitH = logoPlaceholderShape.getScaledHeight() - padding * 2;

          const fitScale = util.findScaleToFit(
            { width: newImg.width, height: newImg.height },
            { width: limitW, height: limitH },
          );

          // 3. Position and Align
          const anchorCenter = selectedObject.getCenterPoint();

          newImg.set({
            left: anchorCenter.x, // Perfectly centered on the anchor
            top: anchorCenter.y,
            scaleX: fitScale,
            scaleY: fitScale,
            originX: "center",
            originY: "center",
            angle: logoPlaceholderShape.angle, // Follows the rectangle's rotation from Illustrator
            layer_name_from_source: selectedObject?.layer_name_from_source,
          });

          const rectCenter = logoPlaceholderShape.getCenterPoint();
          const rectLeft = logoPlaceholderShape.left;
          const rectWidth = logoPlaceholderShape.getScaledWidth();

          let alignmentCmd = CommonEnums.alignment.left;
          const logoShapeName =
            logoPlaceholderShape?.layer_name_from_source ?? "";

          const alignmentRegex = /align-([^\s_<>]+)/;
          const alignmentMatch = selectedLayerName.match(alignmentRegex);

          if (alignmentMatch) {
            alignmentCmd = alignmentMatch[1];
          }

          // Calculate the absolute boundaries
          const absoluteLeft = rectCenter.x - rectWidth / 2;
          const absoluteRight = rectCenter.x + rectWidth / 2;

          if (alignmentCmd === CommonEnums.alignment.left) {
            newImg.set({
              left: absoluteLeft + padding,
              originX: "left",
            });
          }

          if (alignmentCmd === CommonEnums.alignment.right) {
            newImg.set({
              left: absoluteRight - padding,
              originX: "right",
            });
          }

          if (alignmentCmd === CommonEnums.alignment.center) {
            newImg.set({
              left: rectCenter.x,
              originX: "center",
            });
          }

          // 4. Swap in Canvas
          const currentIndex = canvas.getObjects().indexOf(selectedObject);
          canvas.add(newImg);

          if (currentIndex !== -1) {
            canvas.moveObjectTo(newImg, currentIndex);
            // Optional: Keep the original rect hidden for future swaps
            selectedObject.set({ visible: false, selectable: false });
          }

          canvas.setActiveObject(newImg);
          newImg.setCoords();
          canvas.requestRenderAll();

          saveHistory(canvas);
          setSelectedObject(newImg);

          return;
        }

        let clipPathOriginalWidth =
          selectedObject.clipPathOriginalWidth * getTargetScales().x;
        let clipPathOriginalHeight =
          selectedObject.clipPathOriginalHeight * getTargetScales().y;

        const targetWidth = clipPathOriginalWidth;
        const targetHeight = clipPathOriginalHeight;

        const scale = util.findScaleToCover(
          {
            width: newImg.width,
            height: newImg.height,
          },
          { width: targetWidth, height: targetHeight },
        );

        let imageGroupId = selectedObject?.imageGroupId;

        let targetShape = null;

        for (let _idx = 0; _idx < allObjects.length; _idx++) {
          const _obj = allObjects[_idx];

          if (_obj?.imageGroupId == imageGroupId) {
            targetShape = _obj;
            break;
          }
        }

        const targetShapeCenter = targetShape.getCenterPoint();

        newImg.set({
          left: targetShapeCenter.x,
          top: targetShapeCenter.y,
          scaleX: scale,
          scaleY: scale,
          originX: "center",
          originY: "center",
          angle: selectedObject.angle, // Keep the original rotation
          clipPathOriginalWidth: selectedObject.clipPathOriginalWidth,
          clipPathOriginalHeight: selectedObject.clipPathOriginalHeight,
          layer_name_from_source: selectedObject?.layer_name_from_source,
          imageGroupId: imageGroupId,
        });

        // 4. THE FIX: Transfer the clipPath without using .clone()
        if (selectedObject?.clipPath) {
          const imageMask = await targetShape.clone();

          // 2. Configure the imageMask to be the "Static Window"
          imageMask.set({
            absolutePositioned: true, // CRITICAL: Keeps imageMask locked to 800x800 canvas coords
            originX: "center",
            originY: "center",
            left: targetShape.getCenterPoint().x,
            top: targetShape.getCenterPoint().y,
            imageGroupId: imageGroupId,
          });

          newImg.set({ clipPath: imageMask });
        }

        // 5. Swap the objects in the canvas
        const currentIndex = canvas.getObjects().indexOf(selectedObject);
        canvas.add(newImg);

        if (currentIndex !== -1) {
          canvas.moveObjectTo(newImg, currentIndex);
          canvas.remove(selectedObject);
        }

        // 6. Finalize
        canvas.setActiveObject(newImg);
        newImg.setCoords();
        canvas.requestRenderAll();

        saveHistory(canvas);
        setSelectedObject(newImg);
        setSidebarKey((prev) => prev + 1);
      } catch (err) {
        console.error("Replacement Error:", err);
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!canvas) return;

    const loadDesignWithDelay = () => {
      // console.log("imageData >> ", imageData);
      extractAgencyColors();
      // loadAndRenderDesignOnCanvas();
      loadAndRenderDesignOnCanvas().then(() => {
        const isMobile = window.innerWidth <= 1024;
        const wrapper = canvasRef.current?.parentElement;
        if (wrapper) {
          const allCanvases = wrapper.querySelectorAll("canvas");
          allCanvases.forEach((c) => {
            c.style.touchAction = "pan-y";
            if (isMobile) c.style.pointerEvents = "none";
          });
        }
      });
    };

    setCommonState((prev) => {
      return {
        ...prev,
        loadingDesignTemplate: true,
        loadingDesignAssets: false,
      };
    });

    const debounceTimer = setTimeout(() => {
      loadDesignWithDelay();
    }, 1500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [imageData, canvas]);

  const undo = async () => {
    if (historyIndex > 0 && canvas) {
      await canvas.loadFromJSON(history[historyIndex - 1]);
      setHistoryIndex(historyIndex - 1);
      canvas.renderAll();
      setSidebarKey((prev) => prev + 1);
    }
  };

  const redo = async () => {
    if (historyIndex < history.length - 1 && canvas) {
      await canvas.loadFromJSON(history[historyIndex + 1]);
      setHistoryIndex(historyIndex + 1);
      canvas.renderAll();
      setSidebarKey((prev) => prev + 1);
    }
  };

  const updateProp = (prop, value) => {
    if (!selectedObject) return;
    selectedObject.set(prop, value);
    selectedObject.set({ dirty: true });
    if (prop === "fontFamily") {
      document.fonts
        .load(`16px "${value}"`)
        .then(() => canvas.requestRenderAll());
    }
    canvas.requestRenderAll();
    saveHistory(canvas);
    setSidebarKey((prev) => prev + 1);
  };

  // --- REPLACEMENT WITH CLIPPING INHERITANCE ---
  const handleImageReplace = (e) => {
    const file = e.target.files[0];
    processImageReplacement(file);
  };

  //   <style>{`
  //   @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Poppins:wght@400;700&family=Roboto:wght@400;700&family=Lato:wght@400;700&display=swap');
  // `}</style>

  const previewDesign = async () => {
    const image = canvas.toDataURL({
      format: "png",
      quality: 1,
    });

    setPreviewImageState({
      image,
      showPreview: true,
    });

    setIsDrawerOpen(false);
  };

  const downloadDesign = () => {
    if (!canvas) return;
    const image = canvas.toDataURL({
      format: "png",
      quality: 1,
    });
    const link = document.createElement("a");
    link.href = image;
    link.download = "design.png";
    link.click();

    setIsDrawerOpen(false);
  };

  useEffect(() => {
    if (!canvas) return;

    let isUpdating = false;

    const updateMobilePreview = () => {
      if (isUpdating) return; // Prevent re-entry
      isUpdating = true;

      try {
        const image = canvas.toDataURL({ format: "png", quality: 0.8 });
        setMobilePreviewImage(image);
      } catch (e) {}

      setTimeout(() => {
        isUpdating = false;
      }, 500);
    };

    // Sirf 3 seconds ke baad ek baar snapshot lo (initial render ke baad)
    const timer = setTimeout(updateMobilePreview, 3000);

    // Jab user text edit kare ya image change kare, tab update karo
    const handleModified = () => {
      setTimeout(updateMobilePreview, 300);
    };

    canvas.on("object:modified", handleModified);
    canvas.on("text:changed", handleModified);

    return () => {
      clearTimeout(timer);
      canvas.off("object:modified", handleModified);
      canvas.off("text:changed", handleModified);
    };
  }, [canvas, imageData]);

  const openMobilePostPreview = () => {
    const image = canvas.toDataURL({
      format: "png",
      quality: 1,
    });

    setMobilePreviewImage(image);

    setIsDrawerOpen(true);
  };

  let designAssetsLoaderClassname = "hide-element";
  let designLoaderClassname = "hide-element";
  // let designAssetsLoaderClassname = "hide-element";

  if (commonState.loadingDesignAssets) designAssetsLoaderClassname = "";
  if (commonState.loadingDesignTemplate) designLoaderClassname = "";

  return (
    <div className="image-tool-container">
      <div className="preview-header-row">
        <div className="section-title">Post preview</div>
        <div className="preview-header-actions">
          <button
            type="button"
            className="preview-icon-btn preview-btn-design"
            title="Preview"
            onClick={previewDesign}
          >
            <PreviewCircleIcon />
          </button>
          <button
            type="button"
            className="preview-icon-btn preview-btn-download"
            title="Download"
            onClick={downloadDesign}
          >
            <DownloadIcon />
          </button>
        </div>
      </div>
      <p className="section-desc right-grammer-desc">
        A live preview of your marketing content with your agency's branding
        applied
      </p>

      {/* <div className="canvas-container">
        <div className="canvas-frame">
          <canvas ref={canvasRef} />
        </div> */}
      <div
        className={`canvas-container ${
          commonState.loadingDesignTemplate || commonState.loadingDesignAssets
            ? "is-loading"
            : ""
        }`}
      >
        <div className="canvas-frame">
          <canvas ref={canvasRef} />
        </div>

        {commonState.loadingDesignTemplate && (
          <div className="design-loader-overlay">
            <div className="design-loader-spinner" />
            <p className="design-loader-text">Loading design...</p>
          </div>
        )}

        {commonState.loadingDesignAssets && (
          <div className="design-loader-overlay">
            <div className="design-loader-spinner" />
            <p className="design-loader-text">Loading design assets...</p>
          </div>
        )}
      </div>

      {/* <div className="preview-canvas-container">
        <div className="canvas-frame">
          <canvas ref={previewCanvasRef} />
        </div>
      </div> */}

      {isDrawerOpen && (
        <div
          className="drawer-overlay"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      <div
        className={`mobile-bottom-drawer ${isDrawerOpen ? "open" : ""} ${previewImageState.showPreview ? "hidden-by-modal" : ""}`}
      >
        <div
          className="drawer-header"
          onClick={() => openMobilePostPreview(true)}
        >
          <div className="drawer-handle-line"></div>
          <span className="drawer-title">Post preview</span>
        </div>

        <div className="drawer-content">
          <div className="drawer-scroll-area">
            <div className="mobile-preview-card">
              <div className="preview-text-info">
                <h3>Post preview</h3>
                <p>
                  A live preview of your marketing content with your agency's
                  branding applied
                </p>
              </div>

              {mobilePreviewImage ? (
                <div className="mobile-canvas-wrapper">
                  <img
                    src={mobilePreviewImage}
                    alt="Design Preview"
                    className="mobile-preview-snapshot"
                  />
                </div>
              ) : (
                <div className="empty-preview-content">
                  <img
                    src="/icons/pending-icon-2.png"
                    alt="Waiting Icon"
                    className="waiting-icon"
                  />
                  <p>Upload logo to generate the preview</p>
                </div>
              )}

              {mobilePreviewImage && (
                <div className="drawer-actions">
                  <button
                    type="button"
                    className="btn-preview"
                    onClick={previewDesign}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    className="btn-download"
                    onClick={downloadDesign}
                  >
                    Download
                  </button>
                </div>
              )}

              <button
                className="close-drawer-btn"
                type="button"
                onClick={() => setIsDrawerOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <PreviewModal
        open={previewImageState.showPreview}
        onClose={() =>
          setPreviewImageState((prev) => ({ ...prev, showPreview: false }))
        }
        images={previewImageState.image ? [previewImageState.image] : []}
        initialIndex={0}
      />
      {/* <img src={previewImageState.image} alt="" /> */}
    </div>
  );
};

export default React.memo(ImageToolPostPreviewForAgency);
